import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { UtensilsCrossed, Play, Search, X, Flame, Leaf, Sparkles, Star, Camera, Video, MapPin, Phone, Clock, FileDown, ChevronRight } from "lucide-react";
import { api, API } from "../api";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import PromoPopup from "./PromoPopup";
import VerifiedBadge from "./VerifiedBadge";
import BackToTop from "../components/BackToTop";
import DishCard from "./menu/DishCard";
import DishDetail from "./menu/DishDetail";
import { BADGE_LABELS, HIGHLIGHT, mediaUrl, dishPhoto, hasVideo, isFeatured, CommercialBadge, PhotoFallback } from "./menu/shared";

const FILTERS = [
  { key: "consigliati", label: "Consigliati", Icon: Sparkles },
  { key: "best_seller", label: "Best seller", Icon: Star },
  { key: "vegetariano", label: "Vegetariani", Icon: Leaf },
  { key: "piccante", label: "Piccanti", Icon: Flame },
  { key: "foto", label: "Con foto", Icon: Camera },
  { key: "video", label: "Con video", Icon: Video },
];

// Parse "Via Roma 12 · 12–15 / 19–23 · 051 123456" into typed rows.
const parseInfo = (info = "") => info.split(/\s[·|•]\s|\n/).map((s) => s.trim()).filter(Boolean).map((text) => {
  if (/(\+?\d[\d\s./-]{6,})/.test(text) && /tel|\+39|^\d|cell/i.test(text)) return { Icon: Phone, text, href: `tel:${text.replace(/[^\d+]/g, "")}` };
  if (/\d{1,2}[:.]\d{2}|\d{1,2}\s?[–-]\s?\d{1,2}|orari|aperto|chiuso/i.test(text)) return { Icon: Clock, text };
  return { Icon: MapPin, text };
});

// Horizontal scroller with an edge fade so users perceive more content to the right.
function HScroll({ children, className = "", testid }) {
  const ref = useRef(null);
  const [more, setMore] = useState(false);
  const check = () => { const el = ref.current; if (el) setMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 8); };
  useEffect(() => { check(); const el = ref.current; el?.addEventListener("scroll", check, { passive: true }); window.addEventListener("resize", check); return () => { el?.removeEventListener("scroll", check); window.removeEventListener("resize", check); }; }, [children]);
  return (
    <div className="relative">
      <div ref={ref} data-testid={testid} className={`flex overflow-x-auto no-scrollbar snap-x ${className}`}>{children}</div>
      <div className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[#f6f7f5] to-transparent transition-opacity ${more ? "opacity-100" : "opacity-0"}`} />
    </div>
  );
}

export default function PublicMenu() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(null);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => { (async () => {
    try { const [mn, cat] = await Promise.all([api.publicMenu(id), api.allergenCatalog()]); setM(mn.data); setCatalog(cat.data); setActive((mn.data.categories || [])[0]?.id || null); }
    catch { setErr(true); }
  })(); }, [id]);

  const label = (code) => catalog.find((a) => a.code === code)?.label || code;
  const cats = useMemo(() => (m?.categories || []).map((c) => ({ ...c, dishes: (c.dishes || []).filter((d) => d.available !== false).map((d) => ({ ...d, _cat: c.name })) })), [m]);
  const allDishes = useMemo(() => cats.flatMap((c) => c.dishes), [cats]);
  const featured = useMemo(() => allDishes.filter(isFeatured), [allDishes]);
  const dishById = useMemo(() => Object.fromEntries(allDishes.map((d) => [d.id, d])), [allDishes]);

  const matchesFilter = (d) => ({ consigliati: isFeatured(d), best_seller: (d.badges || []).includes("best_seller"), vegetariano: (d.badges || []).includes("vegetariano"), piccante: (d.badges || []).includes("piccante"), foto: !!dishPhoto(d), video: hasVideo(d) }[filter] ?? true);
  const matchesQuery = (d) => { const q = query.trim().toLowerCase(); if (!q) return true; return [d.name, d.description, d.ingredients, d._cat, ...(d.badges || []).map((b) => BADGE_LABELS[b] || b), ...(d.allergens_confirmed || []).map(label)].join(" ").toLowerCase().includes(q); };
  const searchMode = !!query.trim() || !!filter;
  const results = useMemo(() => allDishes.filter((d) => matchesFilter(d) && matchesQuery(d)), [allDishes, filter, query]); // eslint-disable-line

  useEffect(() => {
    if (searchMode || cats.length === 0) return;
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.dataset.cat);
    }, { rootMargin: "-120px 0px -70% 0px", threshold: 0 });
    cats.forEach((c) => { const el = sectionRefs.current[c.id]; if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [cats, searchMode]);

  const goCat = (cid) => { setActive(cid); sectionRefs.current[cid]?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const openDish = (d) => { setOpen(d); setShowVideo(false); };

  if (err) return <div className="min-h-screen grid place-items-center text-muted-foreground">Menù non disponibile.</div>;
  if (!m) return <div className="min-h-screen grid place-items-center text-muted-foreground">Caricamento…</div>;

  const cover = mediaUrl(m.cover, 800);
  const logo = mediaUrl(m.logo, 160);
  const info = parseInfo(m.info);

  return (
    <div className="min-h-screen bg-[#f6f7f5] overflow-x-hidden" data-testid="public-menu">
      <PromoPopup menuId={id} />

      {/* Header */}
      <header className="relative" data-testid="menu-header">
        {cover ? (
          <div className="relative h-36 sm:h-48 w-full overflow-hidden bg-[#0c2d34]">
            <img src={cover} alt="" className="h-full w-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#f6f7f5] via-[#0c2d34]/10 to-transparent" />
          </div>
        ) : (
          <div className="h-20 w-full bg-[#123C46]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />
        )}
        <div className="max-w-2xl mx-auto px-5 -mt-9 relative">
          {logo ? (
            <img src={logo} alt="logo" width="72" height="72" className="h-[72px] w-[72px] rounded-2xl object-cover ring-4 ring-[#f6f7f5] shadow-md bg-white" data-testid="menu-logo" />
          ) : (
            <span className="h-[72px] w-[72px] rounded-2xl bg-[#123C46] grid place-items-center ring-4 ring-[#f6f7f5] shadow-md"><UtensilsCrossed className="h-8 w-8 text-[#8fd7c4]" /></span>
          )}
          <h1 className="font-display text-[26px] sm:text-3xl font-extrabold text-[#0c2d34] leading-[1.15] mt-3 break-words" data-testid="menu-name">{m.name}</h1>
          <VerifiedBadge menuId={m.id} className="mt-2" />
          {m.description && <p className="text-[15px] text-[#3f5b60] mt-1.5 leading-relaxed">{m.description}</p>}
          {info.length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid="menu-info">
              {info.map((row, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[14px] text-[#3f5b60] leading-relaxed">
                  <row.Icon className="h-4 w-4 mt-[3px] shrink-0 text-[#123C46]" />
                  {row.href ? <a href={row.href} className="underline-offset-2 hover:underline">{row.text}</a> : <span className="break-words">{row.text}</span>}
                </li>
              ))}
            </ul>
          )}
          {m.pdf_public && (
            <a href={`${API}/public/menus/${id}/pdf`} target="_blank" rel="noreferrer" data-testid="menu-pdf-download"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#123C46]/20 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-[#123C46] shadow-sm hover:bg-[#123C46] hover:text-white transition-colors">
              <FileDown className="h-4 w-4" /> Scarica menù PDF
            </a>
          )}
        </div>
      </header>

      {/* Search + quick filters */}
      <div className="max-w-2xl mx-auto px-5 mt-5">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#123C46]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca un piatto, un ingrediente…" data-testid="menu-search"
            className="w-full h-12 pl-12 pr-11 rounded-full border border-[#123C46]/15 bg-white text-[15px] text-[#0c2d34] placeholder:text-[#5a7177] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#123C46]/25 focus:border-[#123C46]/30" />
          {query && <button onClick={() => setQuery("")} data-testid="menu-search-clear" aria-label="Cancella" className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-full text-[#5a7177] hover:bg-muted"><X className="h-4 w-4" /></button>}
        </div>
        <HScroll className="gap-2 py-3" testid="menu-filters">
          {FILTERS.map((f) => {
            const on = filter === f.key;
            return (
              <button key={f.key} onClick={() => setFilter(on ? null : f.key)} data-testid={`filter-${f.key}`}
                className={`snap-start inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 h-9 text-[13px] font-semibold border transition-colors ${on ? "bg-[#123C46] text-white border-[#123C46]" : "bg-white text-[#3f5b60] border-border hover:border-[#123C46]/30"}`}>
                <f.Icon className="h-3.5 w-3.5" /> {f.label}
              </button>
            );
          })}
          <span className="shrink-0 w-6" />
        </HScroll>
      </div>

      {/* Featured carousel */}
      {!searchMode && featured.length > 0 && (
        <section className="mt-1 mb-3" data-testid="featured-section">
          <div className="max-w-2xl mx-auto px-5 flex items-center justify-between mb-2.5">
            <h2 className="font-display text-xl font-extrabold text-[#0c2d34] flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> Da provare</h2>
            <span className="text-[12px] text-[#5a7177] inline-flex items-center gap-0.5">Scorri <ChevronRight className="h-3.5 w-3.5" /></span>
          </div>
          <div className="max-w-2xl mx-auto">
            <HScroll className="gap-3 px-5 pb-2" testid="featured-scroll">
              {featured.map((d) => {
                const photo = dishPhoto(d, 400);
                const topBadge = d.recommended && !(d.badges || []).includes("consigliato") ? "consigliato" : (d.badges || []).find((b) => HIGHLIGHT.includes(b));
                return (
                  <button key={d.id} onClick={() => openDish(d)} data-testid={`featured-${d.id}`}
                    className="snap-start shrink-0 w-[200px] text-left rounded-2xl bg-white border border-border/80 overflow-hidden card-shadow hover:shadow-md transition-shadow active:scale-[0.98]">
                    <div className="relative h-[140px] w-full bg-muted">
                      {photo ? <img src={photo} alt={d.name} width="200" height="140" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                        : <PhotoFallback category={d._cat} className="h-full w-full" iconClass="h-8 w-8" />}
                      {hasVideo(d) && <span className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 grid place-items-center"><Play className="h-3.5 w-3.5 text-white fill-white ml-0.5" /></span>}
                      {topBadge && <span className="absolute bottom-2 left-2"><CommercialBadge code={topBadge} size="xs" /></span>}
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-[15px] text-[#0c2d34] leading-snug line-clamp-2 min-h-[2.5rem]">{d.name}</h3>
                      <div className="flex items-center justify-between gap-2 mt-1.5">
                        <span className="text-[11px] text-[#5a7177] truncate">{d._cat}</span>
                        <span className="font-bold text-[15px] text-[#123C46] whitespace-nowrap">{d.price}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
              <span className="shrink-0 w-2" />
            </HScroll>
          </div>
        </section>
      )}

      {/* Sticky category nav */}
      {!searchMode && cats.length > 0 && (
        <div className="sticky top-0 z-20 bg-[#f6f7f5]/92 backdrop-blur-md border-b border-border/70" data-testid="category-nav">
          <div className="max-w-2xl mx-auto">
            <HScroll className="gap-2 px-5 py-2.5" testid="category-scroll">
              {cats.map((c) => (
                <button key={c.id} onClick={() => goCat(c.id)} data-testid={`cat-chip-${c.id}`}
                  className={`snap-start px-4 h-10 rounded-full text-[14px] font-semibold whitespace-nowrap transition-colors ${active === c.id ? "bg-[#123C46] text-white" : "bg-white text-[#3f5b60] border border-border"}`}>
                  {c.name}
                </button>
              ))}
              <span className="shrink-0 w-4" />
            </HScroll>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-2xl mx-auto px-5 py-5">
        {searchMode ? (
          <div data-testid="search-results">
            <p className="text-sm text-muted-foreground mb-3">{results.length} {results.length === 1 ? "risultato" : "risultati"}</p>
            {results.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground"><Search className="h-8 w-8 mx-auto mb-2 opacity-40" /><p className="text-sm">Nessun piatto trovato. Prova un altro termine o filtro.</p></div>
            ) : (
              <div className="space-y-3">{results.map((d) => <DishCard key={d.id} d={d} label={label} onOpen={() => openDish(d)} />)}</div>
            )}
          </div>
        ) : (
          <div className="space-y-9">
            {cats.map((c) => (
              <section key={c.id} id={`c-${c.id}`} data-cat={c.id} ref={(el) => (sectionRefs.current[c.id] = el)} className="scroll-mt-20">
                <div className="mb-3">
                  <h2 className="font-display text-xl font-extrabold text-[#0c2d34]">{c.name}</h2>
                  {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
                </div>
                <div className="space-y-3">{c.dishes.map((d) => <DishCard key={d.id} d={d} label={label} onOpen={() => openDish(d)} />)}</div>
              </section>
            ))}
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground pt-8">Informazioni allergeni fornite dal gestore. Per intolleranze chiedi sempre al personale.</p>
        <p className="text-center text-xs font-bold text-muted-foreground mt-1">⚡ Powered by Qora</p>
      </div>

      <BackToTop offset={88} />
      <Dialog open={!!open} onOpenChange={(o) => { if (!o) { setOpen(null); setShowVideo(false); } }}>
        <DialogContent className="max-w-md p-0 overflow-hidden max-h-[92vh] overflow-y-auto rounded-3xl" data-testid="dish-detail">
          <DialogTitle className="sr-only">{open?.name || "Dettaglio piatto"}</DialogTitle>
          {open && <DishDetail d={open} label={label} showVideo={showVideo} setShowVideo={setShowVideo} dishById={dishById} onOpenSuggested={(sd) => { setShowVideo(false); setOpen(sd); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
